import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';

import * as departApi from '../../../lib/api/department';
import * as employeeApi from '../../../lib/api/employee';

/**
 * 영업부서 / 영업사원 검색 필터.
 *
 * 포캐스팅·프로젝트·유지보수 목록이 같은 조회 API(ForecastSearchDto)를 쓰기 때문에
 * 세 화면이 이 컴포넌트를 공유한다. 화면마다 복사하면 조회 조건이 서로 어긋나기 쉽다.
 *
 * 서버는 부서명/사원명을 LIKE로 검색하므로 폼 값에는 UUID가 아니라 "이름"을 담는다.
 * 사원 목록 조회에만 부서 UUID가 필요해서, 선택된 부서명으로 목록에서 UUID를 되찾아 쓴다.
 *
 * 목록에서 고르는 것과 타이핑으로 걸러내는 것을 모두 지원한다(Autocomplete).
 */
export const SalesDeptUserFilter = ({
  formik,
  deptField = 'searchDpt',
  usrField = 'searchUsr',
  wrapperSx,
}) => {
  // 이 컴포넌트는 grid(포캐스팅)와 flex(프로젝트·유지보수) 검색줄 양쪽에 들어간다.
  // grid에서는 width가, flex에서는 flex-basis가 폭을 정하도록 둘 다 지정한다.
  // 지정하지 않으면 flex 부모에서 내용 크기(=거의 0)로 접혀버린다.
  const fieldSx = { minWidth: 0, width: '100%', flex: '0 1 25ch', ...wrapperSx };
  const [deptList, setDeptList] = useState([]);
  const [usrList, setUsrList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const deptValue = formik.values[deptField] || '';
  const usrValue = formik.values[usrField] || '';

  // 부서 목록은 화면 진입 시 1회만 조회한다.
  useEffect(() => {
    let alive = true;
    departApi
      // 영업 역할이 있는 말단 조직만 뽑는다.
      //   역할  : 부서 수정 화면에서 지정한다. 영업을 안 하는 팀은 검색해도 결과가 없다
      //   말단  : 본부에는 사람이 소속되지 않아 마찬가지로 결과가 안 나온다
      .getListDepartments({ useYn: 'Y', authTyp: 'SALES', leafOnly: true })
      .then((res) => {
        if (alive) setDeptList(res?.data?.list ?? []);
      })
      .catch(() => {
        if (alive) setDeptList([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const selectedDept = deptList.find((d) => d.dptNm === deptValue);

  // 부서가 바뀌면 그 부서의 사원만 다시 불러온다.
  useEffect(() => {
    const dptUuid = selectedDept?.dptUuid;
    if (!dptUuid) {
      setUsrList([]);
      return undefined;
    }
    let alive = true;
    setLoadingUsers(true);
    employeeApi
      .getEmployees({ dptUuid })
      .then((res) => {
        if (alive) setUsrList(res?.data?.list ?? []);
      })
      .catch(() => {
        if (alive) setUsrList([]);
      })
      .finally(() => {
        if (alive) setLoadingUsers(false);
      });
    return () => {
      alive = false;
    };
  }, [selectedDept?.dptUuid]);

  const selectedUsr = usrList.find((u) => u.usrNm === usrValue);

  const handleChangeDept = (event, newValue) => {
    formik.setFieldValue(deptField, newValue?.dptNm ?? '');
    // 부서가 바뀌면 이전 사원 선택은 의미가 없으므로 비운다.
    formik.setFieldValue(usrField, '');
  };

  const handleChangeUsr = (event, newValue) => {
    formik.setFieldValue(usrField, newValue?.usrNm ?? '');
  };

  return (
    <>
      <Box sx={fieldSx}>
        <Autocomplete
          id={deptField}
          options={deptList}
          getOptionLabel={(option) => option?.dptNm ?? ''}
          isOptionEqualToValue={(option, value) => option?.dptUuid === value?.dptUuid}
          value={selectedDept ?? null}
          onChange={handleChangeDept}
          noOptionsText="일치하는 부서가 없습니다"
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="영업부서"
              placeholder="전체"
              error={formik.touched[deptField] && Boolean(formik.errors[deptField])}
              helperText={formik.touched[deptField] && formik.errors[deptField]}
            />
          )}
        />
      </Box>
      <Box sx={fieldSx}>
        <Autocomplete
          id={usrField}
          options={usrList}
          getOptionLabel={(option) =>
            option ? `${option.usrNm ?? ''}${option.gradeNm ? ` (${option.gradeNm})` : ''}` : ''
          }
          isOptionEqualToValue={(option, value) => option?.usrUuid === value?.usrUuid}
          value={selectedUsr ?? null}
          onChange={handleChangeUsr}
          loading={loadingUsers}
          noOptionsText={deptValue ? '해당 부서에 사원이 없습니다' : '부서를 먼저 선택하세요'}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="영업사원"
              placeholder={deptValue ? '전체' : '부서 선택 후'}
              error={formik.touched[usrField] && Boolean(formik.errors[usrField])}
              // 안내문을 helperText로 두면 이 칸만 높이가 커져 같은 줄의 다른 필드와 밑줄이 어긋난다.
              // 안내는 placeholder와 noOptionsText로 하고 helperText는 검증 오류일 때만 쓴다.
              helperText={formik.touched[usrField] && formik.errors[usrField]}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </>
  );
};

export default SalesDeptUserFilter;
